import React from "react";
import {
  Add,
  InfoOutlined,
  ModeEditOutlineTwoTone,
  NextPlan,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  IconButton,
  Popover,
  Tooltip,
  Typography,
} from "@mui/material";
import { Link, useSearchParams, useNavigate } from "react-router-dom";

import AppTable from "components/Shared/AppTable";
import TableFilter from "components/Shared/AppTable/TableFilter";
import CustomTabs from "components/Shared/CustomTabs";
import BreadcrumbLayout from "layouts/BreadcrumbLayout";

import { useLazyListConsolidationQuery } from "store/services/consolidationApi";
import { useAppMeta, useTablePagination } from "hooks";
import HighlightedText from "components/Shared/HighlightedText";
import AppTableDateTime from "components/Shared/AppTableDateTime";

// Component to render the Shipments cell
const ShipmentsCell = ({ shipments, keyword }) => {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [selectedShipments, setSelectedShipments] = React.useState([]);

  const visibleShipments = shipments.slice(0, 2);
  const remainingCount = shipments.length - visibleShipments.length;

  const handleChipClick = (event, allShipments) => {
    setAnchorEl(event.currentTarget);
    setSelectedShipments(allShipments);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  return shipments.length > 0 ? (
    <>
      <Box display="flex" alignItems="center" flexWrap="wrap" gap={0.5}>
        {visibleShipments.map((shipment, index) => {
          const shiId = shipment?.shipmentId
            ?.replace(/(.{4})/g, "$1-")
            .slice(0, -1);
          return (
            <span key={index} style={{ fontSize: "0.9rem" }}>
              <HighlightedText
                text={shipment.IsReturning ? `${shiId} (R)` : shiId}
                highlight={keyword}
              />
              {index !== visibleShipments.length - 1 && ","}
            </span>
          );
        })}
        {remainingCount > 0 && (
          <Tooltip title="Click to view all Shipment IDs">
            <Chip
              label={`+${remainingCount}`}
              size="small"
              sx={{
                marginLeft: "4px",
                height: "18px",
                fontSize: "10px",
                backgroundColor: "#e0e0e0",
                color: "#333",
                cursor: "pointer",
              }}
              onClick={(e) => handleChipClick(e, shipments)}
            />
          </Tooltip>
        )}
      </Box>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
      >
        <Box p={2} minWidth={100}>
          {selectedShipments.map((shipment, index) => (
            <Typography
              key={index}
              variant="body2"
              sx={{ wordBreak: "break-word" }}
            >
              <HighlightedText
                text={
                  shipment.IsReturning
                    ? `${shipment.shipmentId} (R)`
                    : shipment.shipmentId
                }
                highlight={keyword}
              />
            </Typography>
          ))}
        </Box>
      </Popover>
    </>
  ) : (
    "N/A"
  );
};

const ConsolidationListScreen = () => {
  const navigate = useNavigate();
  const { currentPage, perPage, handlePageChange, handlePerRowsChange } =
    useTablePagination();

  const [searchParams] = useSearchParams();

  const activeTab = searchParams.get("tab") || "Active";

  const keyword = searchParams.get("search") || "";

  const breadcrumbs = [{ title: "Consolidations" }];

  const consolidationTabs = [
    { label: "Active", value: "Active" },
    { label: "Inactive", value: "Inactive" },
  ];

  const columns = [
    {
      name: "Consolidation ID",
      selector: (row) => row?.consolidationId,
      cell: (row) => {
        const displayId = row?.consolidationId;
        const path = `/app/crm/consolidation/${row?._id}/detail`;

        return displayId ? (
          <Link
            title={displayId}
            to={path}
            className="line-clamp-2 hover:text-blue-600 hover:underline dark:hover:text-blue-300"
          >
            <HighlightedText text={displayId} highlight={keyword} />
          </Link>
        ) : (
          "N/A"
        );
      },
    },
    {
      name: "Mode",
      selector: (row) => row?.mode,
      width: "220px",
      cell: (row) => {
        const mode = row?.mode;
        let name = "";
        if (mode === "Source") {
          name = row?.route?.routeName
            ? `${row.route.routeName} (Route)`
            : row?.branch?.name
            ? `${row.branch.name} (Branch)`
            : "N/A";
        } else if (mode === "In Transit") {
          name = row?.warehouse?.name ? `${row.warehouse.name} (Warehouse)` : "N/A";
        }

        return (
          <div className="flex flex-col">
            <div className="text-left">
              <HighlightedText text={name} highlight={keyword} />
            </div>
            <div className="mt-1 text-xs text-gray-500 dark:text-gray-300">
              {mode || "N/A"}
            </div>
          </div>
        );
      },
    },
    // {
    //   name: "Source",
    //   selector: (row) => row?.route?.routeName || row?.branch?.name,
    //   cell: (row) => {
    //     if (row?.route?.routeName) {
    //       return `${row.route.routeName} (Route)`;
    //     } else if (row?.branch?.name) {
    //       return `${row.branch.name} (Branch)`;
    //     }
    //     return "N/A";
    //   },
    // },
    {
      name: "Shipment",
      selector: (row) => row.shipmentId,
      cell: (row) => (
        <ShipmentsCell
          shipments={
            Array.isArray(row.shipmentId) ? row.shipmentId : []
          }
          keyword={keyword}
        />
      ),
    },
    // {
    //   name: "Type",
    //   selector: (row) => row?.type,
    //   cell: (row) => row?.type,
    // },
    {
      name: "Name",
      selector: (row) => row?.name,
      cell: (row) => row?.name,
    },
    // {
    //   name: "Load Type",
    //   selector: (row) => row?.loadType,
    //   cell: (row) => row?.loadType,
    // },
    {
      name: "Deconsolidation Status",
      selector: (row) => row?.deconsolidationStatus,
      cell: (row) => row?.deconsolidationStatus,
    },
 {
      name: "Created By",
      selector: (row) => row?.createdBy,
      cell: (row) => row?.createdBy?.name || "N/A",
    },
    {
      name: "Created At",
      selector: (row) => <AppTableDateTime date={row?.createdAt} />,
    },
    {
      name: "Actions",
      cell: (row) => {
        const consolidationShipments = (row.shipmentId || []).filter(
          (shipment) => shipment.status === "Consolidation"
        );

        const isCreateLoadingDisabled = consolidationShipments.length === 0;

        return (
          <Box display="flex" alignItems="center">
            <Link to={`/app/crm/consolidation/${row?._id}/detail`}>
              <IconButton
                size="small"
                aria-label="View Consolidation"
                color="primary"
              >
                <InfoOutlined />
              </IconButton>
            </Link>
            <Link to={`/app/crm/consolidation/${row?._id}/edit`}>
              <IconButton size="small" aria-label="Edit Consolidation">
                <ModeEditOutlineTwoTone />
              </IconButton>
            </Link>
            <Tooltip
              title={
                isCreateLoadingDisabled
                  ? "No shipments in Consolidation status"
                  : "Create Shipment Loading"
              }
            >
              <span>
                <IconButton
                  size="small"
                  aria-label="Create Shipment Loading"
                  onClick={() => {
                    navigate("/app/crm/shipment-loading/create", {
                      state: {
                        consolidation: {
                          _id: row._id,
                          consolidationId: row.consolidationId,
                          name: row.name,
                        },
                        shipments: consolidationShipments,
                      },
                    });
                  }}
                  disabled={isCreateLoadingDisabled}
                  color="primary"
                >
                  <NextPlan />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        );
      },
      ignoreRowClick: true,
      right: true,
    },
  ];

  const [
    fetchConsolidations,
    { data: consolidationRes, isLoading, isFetching, error },
  ] = useLazyListConsolidationQuery();

  React.useEffect(() => {
    fetchConsolidations({
      active: activeTab === "Active",
      page: currentPage,
      limit: perPage,
      ...(keyword && { keyword }),
    });
  }, [activeTab, currentPage, fetchConsolidations, keyword, perPage]);

  useAppMeta(`${activeTab} Consolidations`);

  return (
    <BreadcrumbLayout breadcrumbs={breadcrumbs} headerActions={<PageActions />}>
      <BreadcrumbLayout.Error error={error} />
      <BreadcrumbLayout.Paper>
        <CustomTabs tabs={consolidationTabs} activeTab={activeTab} />
        <TableFilter
          searchLabel="Consolidation ID / Shipment ID"
          disabled={isLoading || isFetching}
        ></TableFilter>
        <AppTable
          columns={columns}
          data={consolidationRes?.data ?? []}
          paginationServer
          progressPending={isLoading || isFetching}
          paginationPerPage={perPage}
          paginationTotalRows={consolidationRes?.totalItems ?? 0}
          paginationDefaultPage={currentPage}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handlePerRowsChange}
        />
      </BreadcrumbLayout.Paper>
    </BreadcrumbLayout>
  );
};

export default ConsolidationListScreen;

const PageActions = () => {
  return (
    <Link to="/app/crm/consolidation/create">
      <Button startIcon={<Add />} variant="contained" color="primary">
        Add Consolidation
      </Button>
    </Link>
  );
};
